import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Panel,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import NodeCard from './NodeCard';
import NodeEditor from './NodeEditor';
import DeleteNodeModal from './DeleteNodeModal';
import supabase from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import Toast from '../ui/Toast';

const nodeTypes = {
  storyNode: NodeCard,
};

const StoryEditor = () => {
  const { id: storyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showNodeEditor, setShowNodeEditor] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [viewportPosition, setViewportPosition] = useState({ x: 0, y: 0, zoom: 1 });
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [layoutCleaned, setLayoutCleaned] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Load story data
  useEffect(() => {
    if (!storyId) return;
    loadStoryData();
  }, [storyId]);

  const loadStoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get story details
      const { data: storyData, error: storyError } = await supabase
        .from('stories_sb2847x9')
        .select('*')
        .eq('id', storyId)
        .single();

      if (storyError) throw storyError;
      setStory(storyData);

      // Get story nodes
      const { data: nodesData, error: nodesError } = await supabase
        .from('story_nodes_sb2847x9')
        .select('*')
        .eq('story_id', storyId);

      if (nodesError) throw nodesError;

      // Get options for edges
      const { data: optionsData, error: optionsError } = await supabase
        .from('options_sb2847x9')
        .select('*')
        .in(
          'story_node_id',
          nodesData.map(node => node.id)
        );

      if (optionsError) throw optionsError;

      // Get node positions
      const { data: positionsData } = await supabase
        .from('node_positions_sb2847x9')
        .select('*')
        .eq('story_id', storyId);

      // Create position map
      const positionsMap = {};
      if (positionsData) {
        positionsData.forEach(pos => {
          positionsMap[pos.node_id] = {
            x: pos.pos_x,
            y: pos.pos_y,
            width: pos.width,
            height: pos.height
          };
        });
      }

      // Create flow nodes
      const flowNodes = nodesData.map((node, index) => {
        const position = positionsMap[node.id] || {
          x: (index % 3) * 300 + 100,
          y: Math.floor(index / 3) * 200 + 100
        };

        return {
          id: node.id,
          type: 'storyNode',
          position,
          data: {
            content: node.content,
            image_url: node.image_url,
            is_start_node: node.is_start_node,
            is_end_node: node.is_end_node,
            onEdit: () => handleEditNode(node.id),
            onDelete: () => handleDeleteNode(node.id),
            onSetStart: () => handleSetStartNode(node.id),
            onSetEnd: () => handleSetEndNode(node.id),
            onUnsetStart: () => handleUnsetStartNode(node.id),
            onUnsetEnd: () => handleUnsetEndNode(node.id),
            onDoubleClick: () => handleEditNode(node.id),
            // Add options data for tooltip
            options: optionsData.filter(opt => opt.story_node_id === node.id)
          },
          style: {
            width: positionsMap[node.id]?.width || 200,
            height: positionsMap[node.id]?.height || 'auto'
          }
        };
      });

      // Create flow edges
      const flowEdges = optionsData
        .filter(option => option.next_node_id) // Only include options with valid next_node_id
        .map(option => ({
          id: `e-${option.id}`,
          source: option.story_node_id,
          target: option.next_node_id,
          label: option.option_text,
          labelStyle: { fontSize: '10px', fill: '#6b21a8' },
          style: { stroke: '#d8b4fe' },
          animated: true,
          markerEnd: {
            type: 'arrowclosed',
            width: 20,
            height: 20,
            color: '#d8b4fe',
          },
        }));

      setNodes(flowNodes);
      setEdges(flowEdges);

      // Auto-cleanup layout when first loading
      if (!layoutCleaned && flowNodes.length > 0) {
        setTimeout(() => {
          handleCleanupLayout(flowNodes, flowEdges);
          setLayoutCleaned(true);
        }, 500);
      }
    } catch (err) {
      console.error('Error loading story data:', err);
      setError('Failed to load story data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle node position updates with error handling
  const saveNodePosition = useCallback(
    async (nodeId, x, y, width, height) => {
      if (!nodeId || !storyId) return;

      try {
        // First check if this position already exists
        const { data: existingPos } = await supabase
          .from('node_positions_sb2847x9')
          .select('id')
          .eq('node_id', nodeId)
          .eq('story_id', storyId)
          .maybeSingle();
        // Use maybeSingle to avoid errors if no record exists

        const positionData = {
          pos_x: x,
          pos_y: y,
          width: width || 200,
          height: height || null
        };

        if (existingPos) {
          // Update existing position
          await supabase
            .from('node_positions_sb2847x9')
            .update(positionData)
            .eq('id', existingPos.id);
        } else {
          // Insert new position
          await supabase
            .from('node_positions_sb2847x9')
            .insert({ story_id: storyId, node_id: nodeId, ...positionData });
        }
      } catch (err) {
        console.error('Error saving node position:', err);
        // Don't throw - we want UI to continue working even if position saving fails
      }
    },
    [storyId]
  );

  // Handle node changes with position saving
  const handleNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => {
        const updatedNodes = applyNodeChanges(changes, nds);

        // Save positions when dragging ends
        changes.forEach(change => {
          if (change.type === 'position' && !change.dragging && change.position) {
            const node = nds.find(n => n.id === change.id);
            if (node) {
              saveNodePosition(
                change.id,
                change.position.x,
                change.position.y,
                node.style?.width,
                node.style?.height
              );
            }
          }
        });

        return updatedNodes;
      });
    },
    [saveNodePosition]
  );

  const handleEditNode = (nodeId) => {
    // Save current viewport position before entering edit mode
    if (reactFlowInstance) {
      const { x, y, zoom } = reactFlowInstance.getViewport();
      setViewportPosition({ x, y, zoom });
    }
    
    setSelectedNodeId(nodeId);
    setShowNodeEditor(true);
  };

  const handleAddNode = () => {
    // Save current viewport position before entering edit mode
    if (reactFlowInstance) {
      const { x, y, zoom } = reactFlowInstance.getViewport();
      setViewportPosition({ x, y, zoom });
    }
    
    setSelectedNodeId('new');
    setShowNodeEditor(true);
  };

  const handleDeleteNode = (nodeId) => {
    setNodeToDelete(nodeId);
    setDeleteModalOpen(true);
  };

  const confirmDeleteNode = async () => {
    if (!nodeToDelete) return;

    try {
      setLoading(true);

      // Delete all options that reference this node
      await supabase
        .from('options_sb2847x9')
        .delete()
        .eq('story_node_id', nodeToDelete);

      await supabase
        .from('options_sb2847x9')
        .delete()
        .eq('next_node_id', nodeToDelete);

      // Delete node position data
      await supabase
        .from('node_positions_sb2847x9')
        .delete()
        .eq('node_id', nodeToDelete);

      // Delete the node itself
      const { error: nodeError } = await supabase
        .from('story_nodes_sb2847x9')
        .delete()
        .eq('id', nodeToDelete);

      if (nodeError) throw nodeError;

      // If this was the start node, clear that reference
      if (story?.start_node_id === nodeToDelete) {
        await supabase
          .from('stories_sb2847x9')
          .update({ start_node_id: null })
          .eq('id', storyId);
      }

      // Update local state
      setNodes(nodes => nodes.filter(n => n.id !== nodeToDelete));
      setEdges(edges => edges.filter(e => e.source !== nodeToDelete && e.target !== nodeToDelete));

      setToast({
        show: true,
        message: 'Node deleted successfully',
        type: 'success'
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) {
      console.error('Error deleting node:', err);
      setError('Failed to delete node. Please try again.');
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
      setNodeToDelete(null);
    }
  };

  const handleSetStartNode = async (nodeId) => {
    try {
      // Unset all existing start nodes
      await supabase
        .from('story_nodes_sb2847x9')
        .update({ is_start_node: false })
        .eq('story_id', storyId);

      // Set this node as start
      await supabase
        .from('story_nodes_sb2847x9')
        .update({ is_start_node: true })
        .eq('id', nodeId);

      // Update story start_node_id
      await supabase
        .from('stories_sb2847x9')
        .update({ start_node_id: nodeId })
        .eq('id', storyId);

      // Refresh data
      loadStoryData();

      setToast({
        show: true,
        message: 'Start node set successfully',
        type: 'success'
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) {
      console.error('Error setting start node:', err);
      setError('Failed to set start node.');
    }
  };

  const handleSetEndNode = async (nodeId) => {
    try {
      await supabase
        .from('story_nodes_sb2847x9')
        .update({ is_end_node: true })
        .eq('id', nodeId);

      loadStoryData();

      setToast({
        show: true,
        message: 'End node set successfully',
        type: 'success'
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) {
      console.error('Error setting end node:', err);
      setError('Failed to set end node.');
    }
  };

  const handleUnsetStartNode = async (nodeId) => {
    try {
      await supabase
        .from('story_nodes_sb2847x9')
        .update({ is_start_node: false })
        .eq('id', nodeId);

      await supabase
        .from('stories_sb2847x9')
        .update({ start_node_id: null })
        .eq('id', storyId);

      loadStoryData();

      setToast({
        show: true,
        message: 'Start node unset successfully',
        type: 'success'
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) {
      console.error('Error unsetting start node:', err);
      setError('Failed to unset start node.');
    }
  };

  const handleUnsetEndNode = async (nodeId) => {
    try {
      await supabase
        .from('story_nodes_sb2847x9')
        .update({ is_end_node: false })
        .eq('id', nodeId);

      loadStoryData();

      setToast({
        show: true,
        message: 'End node unset successfully',
        type: 'success'
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (err) {
      console.error('Error unsetting end node:', err);
      setError('Failed to unset end node.');
    }
  };

  const handleCleanupLayout = useCallback(
    (nodeList = nodes, edgeList = edges) => {
      const startNodes = nodeList.filter(n => n.data.is_start_node);
      if (startNodes.length === 0 && nodeList.length > 0) {
        // If no start node, use the first node as reference
        startNodes.push(nodeList[0]);
      }

      if (startNodes.length === 0) return;

      const startNode = startNodes[0];
      const processed = new Set();
      const levels = new Map();
      const children = new Map();

      // Build adjacency list
      edgeList.forEach(edge => {
        if (!children.has(edge.source)) {
          children.set(edge.source, []);
        }
        children.get(edge.source).push(edge.target);
      });

      // BFS to assign levels
      const queue = [[startNode.id, 0]];
      processed.add(startNode.id);
      levels.set(startNode.id, 0);

      while (queue.length > 0) {
        const [nodeId, level] = queue.shift();
        const nodeChildren = children.get(nodeId) || [];
        nodeChildren.forEach(childId => {
          if (!processed.has(childId)) {
            processed.add(childId);
            levels.set(childId, level + 1);
            queue.push([childId, level + 1]);
          }
        });
      }

      // Position nodes based on levels - vertically oriented
      // Increased spacing for better visibility
      const VERTICAL_SPACING = 300;
      const HORIZONTAL_SPACING = 500;
      const levelGroups = new Map();

      levels.forEach((level, nodeId) => {
        if (!levelGroups.has(level)) {
          levelGroups.set(level, []);
        }
        levelGroups.get(level).push(nodeId);
      });

      // Handle unconnected nodes
      nodeList.forEach(node => {
        if (!levels.has(node.id)) {
          const maxLevel = Math.max(...Array.from(levels.values()), 0);
          levels.set(node.id, maxLevel + 1);
          if (!levelGroups.has(maxLevel + 1)) {
            levelGroups.set(maxLevel + 1, []);
          }
          levelGroups.get(maxLevel + 1).push(node.id);
        }
      });

      // Update nodes with new positions
      const newNodes = nodeList.map(node => {
        const level = levels.get(node.id);
        const nodesInLevel = levelGroups.get(level);
        const indexInLevel = nodesInLevel.indexOf(node.id);

        // Y position based on level (top to bottom)
        const y = level * VERTICAL_SPACING + 100;

        // X position centered and spread horizontally
        const centerX = window.innerWidth / 2;
        const totalWidth = (nodesInLevel.length - 1) * HORIZONTAL_SPACING;
        const x = centerX - totalWidth / 2 + indexInLevel * HORIZONTAL_SPACING;

        const newNode = { ...node, position: { x, y } };

        // Update the node positions in React Flow
        setNodes(nodes =>
          nodes.map(n => (n.id === node.id ? { ...n, position: { x, y } } : n))
        );

        // Update position in database - but don't wait for it
        try {
          saveNodePosition(node.id, x, y, node.style?.width, node.style?.height);
        } catch (err) {
          console.error('Error saving node position during cleanup:', err);
        }

        return newNode;
      });

      setToast({
        show: true,
        message: 'Layout cleaned up successfully',
        type: 'success'
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    },
    [saveNodePosition, nodes, edges]
  );

  const handleNodeSave = (savedNode) => {
    // Check if this is a navigation action
    if (savedNode && savedNode.action === 'navigate') {
      // Keep the editor open but change the node ID
      setSelectedNodeId(savedNode.id);
      // Show a toast notification
      setToast({
        show: true,
        message: 'Loading node...',
        type: 'info'
      });
      setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 2000);
      return;
    }
    
    // Normal save action - return to map
    setShowNodeEditor(false);
    setSelectedNodeId(null);
    
    // Force a full reload of story data to ensure new node appears immediately
    loadStoryData();
    
    // Restore the previous viewport position after saving
    if (reactFlowInstance) {
      setTimeout(() => {
        reactFlowInstance.setViewport(viewportPosition);
      }, 100);
    }
  };

  const handleNodeCancel = () => {
    setShowNodeEditor(false);
    setSelectedNodeId(null);

    // Restore the previous viewport position after canceling
    if (reactFlowInstance) {
      setTimeout(() => {
        reactFlowInstance.setViewport(viewportPosition);
      }, 100);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handlePreview = () => {
    navigate(`/story/${storyId}/preview`);
  };

  const onInit = (instance) => {
    setReactFlowInstance(instance);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <p className="ml-4 text-gray-600">Loading story editor...</p>
      </div>
    );
  }

  if (showNodeEditor) {
    return (
      <NodeEditor
        storyId={storyId}
        nodeId={selectedNodeId}
        onSave={handleNodeSave}
        onCancel={handleNodeCancel}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="story-editor bg-white rounded-3xl shadow-dreamy p-6"
      style={{ height: '80vh' }}
    >
      <div className="flex justify-between items-center mb-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2"
          onClick={handleBack}
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="text-lg" />
          Back to Stories
        </motion.button>

        <h2 className="text-2xl font-display font-bold text-purple-700">
          {story?.title ? `Edit: ${story.title}` : 'Story Editor'}
        </h2>

        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-mint-100 hover:bg-mint-200 text-mint-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2"
            onClick={handlePreview}
          >
            <SafeIcon icon={FiIcons.FiEye} className="text-lg" />
            Preview
          </motion.button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
          <button
            className="mt-2 bg-red-200 hover:bg-red-300 text-red-800 px-3 py-1 rounded-md"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div style={{ height: 'calc(100% - 120px)' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-right"
          onInit={onInit}
          defaultViewport={viewportPosition}
        >
          <Controls />
          <MiniMap />
          <Background color="#f3e8ff" gap={16} />
          <Panel position="top-left">
            <div className="flex gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-xl font-medium flex items-center gap-2"
                onClick={handleAddNode}
              >
                <SafeIcon icon={FiIcons.FiPlus} className="text-lg" />
                Add Node
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2"
                onClick={() => handleCleanupLayout()}
              >
                <SafeIcon icon={FiIcons.FiGrid} className="text-lg" />
                Cleanup Layout
              </motion.button>
            </div>
          </Panel>
          <Panel position="top-right">
            <div className="bg-white p-3 rounded-lg shadow-soft">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
                <span className="text-xs">Start Node</span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-purple-400"></div>
                <span className="text-xs">Story Node</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <span className="text-xs">End Node</span>
              </div>
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      )}

      <DeleteNodeModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDeleteNode}
      />
    </motion.div>
  );
};

export default StoryEditor;